import Backbone from 'backbone'
import template from './record-form.hbs'
import _ from 'lodash'
import formHelper from 'scoreboard/components/form-helper'
import FormView from 'scoreboard/components/form-view'
import $ from 'jquery'
import FieldValueControl from './field-value-control'
import getFieldValues from './get-field-values'
import moveDecimal from 'scoreboard/components/move-decimal'
import RecordUtil from 'scoreboard/datasets/records/record-util'
const { isSupplementalOnly, isNewRecord } = RecordUtil
import createDatasetRecord from 'scoreboard/datasets/model/dataset-record'
import DataType from 'scoreboard/datasets/model/data-type'
const { PERCENT } = DataType

var RecordForm = Backbone.View.extend({

	tagName: 'form',

	className: 'record-form',

	events: {
		'click .undo-all-edits': 'undoAllEdits'
	},

	/**
	 * Form used to create/edit records on the Datasets | Records tab.
	 *
	 * @constructs
	 * @property {Dataset} model
	 * @property {DatasetRecord} record
	 * @property {Object<Number,Object>} treeItemsById is a hash where keys are
	 * tree IDs and values are tree items
	 * @property {FieldValueControl[]} fieldValueControls
	 *
	 * @param {Object} options
	 * @param {DatasetRecord} record
	 */
	initialize: function(options) {
		this.options = options || {}
		this.record = this.options.record
		this.fieldValueControls = []
		this.formView = new FormView({
			el: this.el,
			submit: this.submit.bind(this),
			guard: false
		})
		// #10624 re-enable undo all edits if something changed
		this.formView.on('change', () => {
			this.$('.undo-all-edits').prop('disabled', false)
		})

		this.filteredFields = this.filterFields()
	},

	render: function() {
		this.$el.empty().append(template(this.context()))

		// do this BEFORE renderControls
		this.initializedUndoAllEdits()
		this.renderControls()
		this.disablePrimaryKey()
		this.formView.start()

		return this
	},

	// render fancy UI controls like date selectors and combo boxes
	renderControls: function() {
		let data = getFieldValues(this.model, this.record)
		const suggestions = this.getFieldValueControlSuggestions()
		_.each(this.filteredFields, field => {
			let value = data[field.id]
			let multiline = _.includes(value, '\n')
			const fieldValueControlOptions = {
				model: this.model,
				suggestions,
				fieldId: field.id,
				value,
				treeItemsById: this.options.treeItemsById,
				record: this.record,
				formView: this.formView,
				label: field.label,
				multiline
			}
			if (!!field.primaryKey) {
				fieldValueControlOptions.required = true
			}
			let control = new FieldValueControl(fieldValueControlOptions)
			this.fieldValueControls.push(control)
			this.$(`.dialog-body-content`).append(control.el)
			control.render()
		})
	},

	getFieldValueControlSuggestions: function() {
		const suggestions = {}
		_.forOwn(this.record.suggestions, (values, key) => {
			suggestions[key] = _.map(values, value => ({
				value: value || '',
				label: value || ''
			}))
		})
		return suggestions
	},

	// disable editing the primary key for existing records
	disablePrimaryKey: function() {
		// the primary key isn't modifiable on existing records
		if (!isSupplementalOnly(this.record)) {
			const pkField = _.find(this.model.get('fields'), function(field) {
				if (field.primaryKey) {
					return true
				}
			})
			if (_.isNil(pkField)) return

			this.$('[name=' + pkField.id + ']').prop('disabled', true)
			this.$('[data-id=' + pkField.id + '] .edited-undo').addClass('is-not-displayed')
		}
	},

	filterFields: function() {
		return _.chain(this.model.get('fields'))
			.filter(function(field) {
				// all fields are editable for supplemental-only records
				return isSupplementalOnly(this.record) || field.editable
			}.bind(this))
			// #16373 hide fields non-admins shouldn't be able to edit
			.filter(field => this.model.isModifyDataset() || !field.hideFromNonAdmins)
			// #34475 filter out the Record Manually/Deleted Added field
			.filter(field => !this.model.isManuallyAddedFieldId(field.id))
			// #39928 cluster fields are generated and cannot be entered manually
			.filter(field => field.fieldType !== 'cluster')
			// #36447 calculated fields' equation will be evaluated, so they cannot be entered manually
			.filter(field => field.fieldType !== 'calculated')
			.sortBy(function(field) {
				return field.label.toLowerCase()
			})
			.value()
	},

	isManuallyAddedField: function(field) {
		// #35460 check manuallyAddedFieldId in the root dataset
		const rootId = this.model.get('rootDatasetId')
		if (!rootId) {
			return false
		}
		const rootDataset = this.model.datasetsById[rootId]
		if (!rootDataset) {
			return false
		}

		return field.id == rootDataset.get('manuallyAddedFieldId')
	},

	context: function() {
		return {
			'new': isNewRecord(this.record),
			fields: this.filteredFields
		}
	},

	// initialize the edited and undo buttons, and the undo all edits button
	initializedUndoAllEdits: function() {
		// #35077 for manually added records, this button makes no sense
		if (isSupplementalOnly(this.record)) {
			this.$('.undo-all-edits').remove()
			return
		}

		// when first creating a record, don't display all the edit/undo stuff
		if (isNewRecord(this.record)) {
			this.$('.undo-all-edits').addClass('is-not-displayed')
		} else {
			// #10624 Disable "undo all edits" button when there's nothing to undo
			var somethingEdited = _.find(this.record.edited, function(edited) {
				return edited
			})
			if (!somethingEdited) {
				this.$('.undo-all-edits').prop('disabled', true)
			}
		}
	},

	undoAllEdits: function() {
		_.invokeMap(this.fieldValueControls, 'undo')

		// #10624 after undoing all edits, there's nothing left to undo so
		// disable the button.  formView triggers change in process.nextTick so
		// we have to do this in nextTick as well
		process.nextTick(() => {
			this.$('.undo-all-edits').prop('disabled', true)
		})
	},

	submit: function() {
		const originalBaseRecord = _.get(this.options, 'record')
		const originalRecord = _.get(originalBaseRecord, 'record')
		const newValue = isSupplementalOnly(this.record)
		const insert = _.isEmpty(this.options.record.record)
		const datasetRecord = createDatasetRecord({
			autogeneratedId: _.isEmpty(originalBaseRecord) ?
				null : originalBaseRecord.autogeneratedId,
			record: [],
			originalRecord,
			edited: [],
			new: newValue,
			insert,
			generatedKey: _.get(originalBaseRecord, 'generatedKey')
		})
		const fields = this.model.get('fields')
		_.forEach(fields, function(field, index) {
			const { value, edited } = this.getValueAndEdited(index, field)
			datasetRecord.record.push(value)
			datasetRecord.edited.push(edited)
		}.bind(this))

		const recordsRequest = {
			fieldIds: _.map(this.model.get('fields'), field => field.id),
			records: [datasetRecord]
		}
		$.post({
			url: 'api/datasets/' + this.model.id + '/records',
			data: JSON.stringify(recordsRequest),
			success: function() {
				this.trigger('done', datasetRecord)
			}.bind(this),
			error: this.formView.error.bind(this.formView)
		})
	},

	getValueAndEdited(fieldIndex, field) {
		const valueAndEdited = this._getRawValueAndEdited(fieldIndex, field)
		const isManuallyAddedField = this.isManuallyAddedField(field)
		const isNew = isSupplementalOnly(this.record)
		const insert = _.isEmpty(this.options.record.record)

		// if this is the first time we're saving a new record, then everything
		// is edited
		if (!isManuallyAddedField && (isNew && insert)) {
			valueAndEdited.edited = true
		}

		const underlyingField = this.model.getUnderlyingField(field)
		if (underlyingField.dataType === PERCENT && !_.isEmpty(valueAndEdited.value)) {
			valueAndEdited.value = moveDecimal(valueAndEdited.value, -2)
		}

		// #38048 and #49745 if readFromDom reads an empty string, set it to null so checks
		// on the server for a null value work properly
		if (_.isNil(valueAndEdited.value)) {
			valueAndEdited.value = null
		}

		return valueAndEdited
	},

	_getRawValueAndEdited(fieldIndex, field) {
		const isNew = isSupplementalOnly(this.record)
		const isManuallyAddedField = this.isManuallyAddedField(field)
		// manually added field always is set to these values and never "edited"
		if (isManuallyAddedField) {
			return {
				value: isNew,
				edited: false
			}
		}

		const edited = _.get(this.options, 'record.edited', {})[fieldIndex]
		const fromDom = formHelper.readFromDom(this.el)

		// if this isn't a calculated field, and we've enabled editing on it
		const editableOrManuallyAdded = field.editable || isNew
		if (field.fieldType !== 'calculated' && editableOrManuallyAdded) {
			const value = fromDom[field.id]
			return {
				value,
				edited: !!edited
			}
		}

		return {
			value: _.get(this.record, 'record.' + fieldIndex),
			edited: _.get(this.record, 'edited.' + fieldIndex)
		}
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap(this.fieldValueControls, 'remove')
		_.invokeMap([this.formView], 'remove')
	}

})

export default RecordForm

RecordForm.__filename__ = 'datasets/records/record-form.js'
